import * as React from 'react';
import { KeyboardEvent, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { use$ } from '@legendapp/state/react';
import { Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import styled, { css } from 'styled-components';

import { landscapeMediaMixin } from '../../hooks/use-vertical-breakpoint';

import { SliderMagicMixin } from './option-selection-wrapper';

import translate from '../../utils/translate';

import { ContentId, ProductView, TRACKING_TIMER, TrackingEventName } from '../../store/stores/tracking/types';

import {
	containerStoreActions,
	degreeImageStore,
	fullscreenStore,
	interiorStore,
	interiorStoreActions,
	rootStore,
	rootStoreActions,
	sliderStore
} from '../../store';
import { trackingStore } from '../../store/stores/tracking-store';
import { Option } from './option';
import { PickerType } from './picker';
import Slider from './slider';

interface OptionConfig {
	type: PickerType;
	label: string;
	data: OptionData[];
	onSelect: (code: string, ref?: RefObject<HTMLButtonElement>) => void;
	getCurrentActive: () => string;
}

interface OptionData {
	code: string;
	name: string;
	assets: string;
	isActive?: boolean;
}

interface OptionSelectionProps {
	pickerType: PickerType;
}

export const OptionSelection = ({ pickerType }: OptionSelectionProps) => {
	const optionRef = useRef<HTMLDivElement>(null);
	const optionRefs = useRef<Map<number, RefObject<HTMLButtonElement>>>(new Map());
	const [isValueExpanded, setIsValueExpanded] = useState(false);
	const isValueExpandable = pickerType === PickerType.Rim || pickerType === PickerType.Interior;
	const sectionHeaderRef = useRef<HTMLDivElement>(null);
	const collapsedHeaderHeight = useRef(0);

	const isDegreeLoading = use$(degreeImageStore.isImageLoaded);

	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);
	const isDoubleSliderVisible = use$(sliderStore.isDoubleSliderVisible);
	const interiorLabel = translate('global.interiorOptionLabel');

	const optionConfigs: OptionConfig[] = [
		{
			type: PickerType.Color,
			label: translate('global.colorLabel'),
			data: getColorOptions(),
			onSelect: code => {
				handleColorSelection(code);
			},
			getCurrentActive: () => getCurrentColorCode()
		},

		{
			type: PickerType.Rim,
			label: translate('global.rimLabel'),
			data: getRimOptions(),
			onSelect: code => {
				handleRimSelection(code);
			},
			getCurrentActive: () => getCurrentRimCode()
		},
		{
			type: PickerType.Interior,
			label: translate('global.interiorLabel'),
			data: getInteriorOptions(),
			onSelect: code => {
				handleInteriorSelection(code);
			},
			getCurrentActive: () => getCurrentInteriorCode()
		}
	];

	useLayoutEffect(() => {
		if (!isValueExpandable || !sectionHeaderRef.current) return;

		if (!isValueExpanded) {
			collapsedHeaderHeight.current = sectionHeaderRef.current.offsetHeight;
			containerStoreActions.setBottomPanelExpansion(0);
			return;
		}

		containerStoreActions.setBottomPanelExpansion(
			Math.max(0, sectionHeaderRef.current.offsetHeight - collapsedHeaderHeight.current)
		);
	});

	useEffect(
		() => () => {
			if (isValueExpandable) containerStoreActions.setBottomPanelExpansion(0);
		},
		[isValueExpandable]
	);

	const activeConfig = optionConfigs.find(config => config.type === pickerType);

	if (!activeConfig) return null;

	const getOptionRef = (index: number): RefObject<HTMLButtonElement> => {
		if (!optionRefs.current.has(index)) {
			optionRefs.current.set(index, { current: null });
		}
		return optionRefs.current.get(index)!;
	};

	const options = activeConfig.data.map((item, index) => {
		const isActive = item.code === activeConfig.getCurrentActive();
		const optionsRef = getOptionRef(index);
		const displayName = item.name || (activeConfig.type === PickerType.Interior ? interiorLabel : '');
		const baseAriaLabel = `${isActive ? 'Active' : 'Inactive'} ${activeConfig.type}`;
		const ariaLabel = displayName ? `${baseAriaLabel}: ${displayName}` : baseAriaLabel;

		return {
			key: item.code,
			ref: optionsRef,
			ariaBusy: isDegreeLoading,
			ariaLabel,
			testId: `${activeConfig.type}_Option_${item.code}`,
			thumbnailUrl: item.assets,
			altText: displayName,
			onClick: () => activeConfig.onSelect(item.code, optionsRef),
			selectedOption: isActive
		};
	});

	// @TODO this is baaaad! it happens on every render
	let activeOptionRef: RefObject<HTMLButtonElement> | undefined;
	let activeOptionText = '';
	if (activeConfig) {
		const activeItemCode = activeConfig.getCurrentActive();
		const activeItemIndex = activeConfig.data.findIndex(item => item.code === activeItemCode);

		activeOptionText =
			options[activeItemIndex]?.altText || (activeConfig.type === PickerType.Interior ? interiorLabel : '');
		if (activeItemIndex !== -1) {
			activeOptionRef = optionRefs.current.get(activeItemIndex);
		}
	}

	return (
		<OptionsLabelWrapper $isDoubleSliderVisible={isDoubleSliderVisible}>
			<OptionsWrapper ref={optionRef} $isFullscreenMode={isFullscreenMode}>
				<Slider parentRef={optionRef} activeOptionOrPickerRef={activeOptionRef} showArrows>
					{options.map(({ key, ...optionProps }) => (
						<Option key={key} {...optionProps} />
					))}
				</Slider>
			</OptionsWrapper>
			<SectionHeader ref={sectionHeaderRef} $isFullscreenMode={isFullscreenMode}>
				<SectionTitle staticSize appearance={TokenTextAppearance.headline100}>
					{activeConfig.label}
				</SectionTitle>
				<SectionValue
					staticSize
					appearance={TokenTextAppearance.copy200}
					aria-live="polite"
					$isExpandable={isValueExpandable}
					$isExpanded={isValueExpandable && isValueExpanded}
					{...(isValueExpandable && {
						title: activeOptionText,
						role: 'button',
						tabIndex: 0,
						'aria-expanded': isValueExpanded,
						onClick: () => setIsValueExpanded(expanded => !expanded),
						onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								setIsValueExpanded(expanded => !expanded);
							}
							if (event.key === 'Escape') {
								setIsValueExpanded(false);
							}
						}
					})}
				>
					{activeOptionText || <>&nbsp;</>}
				</SectionValue>
			</SectionHeader>
		</OptionsLabelWrapper>
	);
};

// @TODO this is baaad!
const getColorOptions = (): OptionData[] => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	const [currentVariantKey] = use$(rootStore.currentVariant);
	const [colorKey] = currentVariantKey.split('-');

	const existingExterior = currentModelValue.exterior
		? Object.keys(currentModelValue.exterior).map(key => key.split('-')[0])
		: [];

	const existingExteriorSet = new Set(existingExterior);

	return Object.entries(currentModelValue?.thumbnailColor || {})
		.filter(([code]) => existingExteriorSet.has(code))
		.map(([code, data]) => ({
			code,
			name: data.name,
			assets: data.assets,
			isActive: code === colorKey
		}));
};

const getRimOptions = (): OptionData[] => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	const [currentVariantKey] = use$(rootStore.currentVariant);
	const [, rimKey] = currentVariantKey.split('-');

	const existingExterior = currentModelValue.exterior
		? Object.keys(currentModelValue.exterior).map(key => key.split('-')[1])
		: [];

	const existingExteriorSet = new Set(existingExterior);

	return Object.entries(currentModelValue?.thumbnailRim || {})
		.filter(([code]) => existingExteriorSet.has(code))
		.map(([code, data]) => ({
			code,
			name: data.name,
			assets: data.assets,
			isActive: code === rimKey
		}));
};

const getInteriorOptions = (): OptionData[] => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	const [currentInteriorKey] = use$(interiorStore.currentInterior);

	return Object.entries(currentModelValue?.thumbnailInterior || {}).map(([code, data]) => ({
		code,
		name: data.name,
		assets: data.assets,
		isActive: code === currentInteriorKey
	}));
};

const getVariantAssets = (variantKey: string): [string[], string] => {
	const [_, currentModelValue] = rootStore.currentModel.get();

	const exteriorObj = currentModelValue.exterior?.[variantKey] || ({} as { assets: string[]; baseUrl: string });

	return [exteriorObj.assets || [], exteriorObj.baseUrl || ''];
};

const handleColorSelection = (colorCode: string) => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [, rimKey] = currentVariantKey.split('-');
	const [_, currentModelValue] = rootStore.currentModel.get();
	const exteriorKeys = Object.keys(currentModelValue.exterior || {});

	const newVariantKey =
		exteriorKeys.find(key => key === `${colorCode}-${rimKey}`) ??
		exteriorKeys.find(key => key.split('-')[0] === colorCode);
	if (!newVariantKey) return;

	const [assets, baseUrl] = getVariantAssets(newVariantKey);

	rootStoreActions.setVariantData([newVariantKey, assets, baseUrl]);

	trackingStore.setTrackingTimer(TRACKING_TIMER.COLOR_SELECTION_NAME, TrackingEventName.VISUALIZER_COLOR_CLICK, {
		contentId: ContentId.MENU,
		sectionName: ProductView.EXTERNAL_VIEW
	});
};

const handleRimSelection = (rimCode: string) => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [colorKey] = currentVariantKey.split('-');
	const [_, currentModelValue] = rootStore.currentModel.get();
	const exteriorKeys = Object.keys(currentModelValue.exterior || {});

	const newVariantKey =
		exteriorKeys.find(key => key === `${colorKey}-${rimCode}`) ??
		exteriorKeys.find(key => key.split('-')[1] === rimCode);
	if (!newVariantKey) return;

	const [assets, baseUrl] = getVariantAssets(newVariantKey);

	rootStoreActions.setVariantData([newVariantKey, assets, baseUrl]);

	trackingStore.setTrackingTimer(TRACKING_TIMER.RIM_SELECTION_NAME, TrackingEventName.VISUALIZER_WHEELS_CLICK, {
		contentId: ContentId.MENU,
		sectionName: ProductView.EXTERNAL_VIEW
	});
};

const handleInteriorSelection = (interiorCode: string) => {
	const [_, currentModelValue] = rootStore.currentModel.get();
	if (currentModelValue.interior && currentModelValue.interior[interiorCode]) {
		interiorStoreActions.setInteriorData([interiorCode, currentModelValue.interior[interiorCode].assets]);
	}

	trackingStore.setTrackingTimer(
		TRACKING_TIMER.PRODUCTVIEW_INTERIOR_NAME,
		TrackingEventName.VISUALIZER_INTERIOR_CLICK,
		{
			sectionName: ProductView.INTERIOR_VIEW,
			contentId: ContentId.MENU
		}
	);
};

const getCurrentColorCode = (): string => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [colorKey] = currentVariantKey.split('-');
	return colorKey || '';
};

const getCurrentRimCode = (): string => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [, rimKey] = currentVariantKey.split('-');
	return rimKey || '';
};

const getCurrentInteriorCode = (): string => {
	const [currentInteriorKey] = interiorStore.currentInterior.get();
	return currentInteriorKey || '';
};

const OptionsLabelWrapper = styled.div<{ $isDoubleSliderVisible: boolean }>`
	${props => SliderMagicMixin(props.$isDoubleSliderVisible)}

	overflow: hidden;

	&& {
		min-width: 228px;
	}
`;

const SectionHeader = styled.div<{ $isFullscreenMode: boolean }>`
	display: flex;
	align-items: baseline;
	gap: 8px;
	overflow: hidden;
	width: 0;
	min-width: 100%;

	@media ${landscapeMediaMixin} {
		display: none;
	}

	${({ $isFullscreenMode }) => $isFullscreenMode && 'display:none;'}
`;

const SectionTitle = styled(Text)`
	flex-shrink: 0;

	&& {
		color: #1b2236;
		font-weight: 700;
		font-size: 18px;
		line-height: 1.2;
	}
`;

const SectionValue = styled(Text)<{ $isExpandable?: boolean; $isExpanded?: boolean }>`
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	${({ $isExpandable }) =>
		$isExpandable &&
		css`
			cursor: pointer;
			flex: 1 1 0%;
			-webkit-tap-highlight-color: transparent;

			&:focus-visible {
				outline: 2px solid #c86c03;
				outline-offset: -3px;
				border-radius: 2px;
			}
		`}

	${({ $isExpanded }) =>
		$isExpanded &&
		css`
			white-space: normal;
			text-overflow: clip;
			overflow: visible;
		`}

	&& {
		color: #1b2236;
		font-weight: 400;
		font-size: 14px;
		line-height: 1.6;
		padding-block: 2px;
	}
`;

const OptionsWrapper = styled.div<{ $isFullscreenMode: boolean }>`
	display: flex;
	justify-content: start;

	${({ $isFullscreenMode }) => $isFullscreenMode && 'justify-content: end;'} @media ${landscapeMediaMixin} {
		justify-content: end;
	}
`;
